import React, { useState, useCallback } from 'react';
import Layout from './Layout';
import { Download, FileSpreadsheet, AlertCircle, CheckCircle2, TrendingUp, DollarSign, Leaf, PlayCircle, FileDown } from 'lucide-react';
import { useData, InputData, CalculationResults } from '../context/DataContext';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';

const DataUpload: React.FC = () => {
  const { inputData, results, updateData, setScenarioName } = useData();
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Calculation engine
  const calculateResults = useCallback((data: InputData): CalculationResults => {
    // CO₂ savings calculation (simplified model)
    const rdfCO2Factor = 0.85; // tCO2/ton RDF
    const biogasCO2Factor = 0.65; // tCO2/MWh
    const clinkerCO2Factor = 0.87; // tCO2/ton clinker avoided
    const steelScrapCO2Factor = 1.2; // tCO2/ton scrap steel
    const cementScrapCO2Factor = 0.9; // tCO2/ton scrap cement
    const fuelCO2Factor = 2.3; // tCO2/ton fossil fuel displaced

    const co2Savings = 
      (data.rdfAvailable * rdfCO2Factor) +
      (data.biogasIntegration * biogasCO2Factor) +
      (data.clinkerSubstitution * clinkerCO2Factor) +
      (data.scrapRatios.steel * steelScrapCO2Factor) +
      (data.scrapRatios.cement * cementScrapCO2Factor) +
      (data.fossilFuelDisplacement * fuelCO2Factor);

    // Financial calculations
    const carbonPricePerTon = 45; // USD
    const operationalSavingsRate = 0.15; // 15% operational cost savings
    const baseOperationalCost = 100000; // USD base cost

    const costSavings = baseOperationalCost * operationalSavingsRate * (
      (data.rdfAvailable + data.biogasIntegration + data.fossilFuelDisplacement) / 1000
    );
    
    const revenue = co2Savings * carbonPricePerTon;
    const netBenefit = revenue + costSavings;

    // Credit potential
    const carbonCredits = co2Savings * 0.9; // 90% eligible for credits
    const itmoValue = carbonCredits * carbonPricePerTon * 1.1; // 10% premium for ITMOs

    return {
      co2Savings,
      financialOutcomes: {
        costSavings,
        revenue,
        netBenefit
      },
      creditPotential: {
        carbonCredits,
        itmoValue
      }
    };
  }, []);

  // Data validation
  const validateData = (data: InputData): string[] => {
    const errors: string[] = [];
    
    if (data.rdfAvailable < 0 || data.rdfAvailable > 100) {
      errors.push('RDF available must be between 0-100%');
    }
    if (data.biogasIntegration < 0 || data.biogasIntegration > 100) {
      errors.push('Biogas integration must be between 0-100%');
    }
    if (data.clinkerSubstitution < 0 || data.clinkerSubstitution > 100) {
      errors.push('Clinker substitution must be between 0-100%');
    }
    if (data.scrapRatios.steel < 0 || data.scrapRatios.steel > 100) {
      errors.push('Steel scrap ratio must be between 0-100%');
    }
    if (data.scrapRatios.cement < 0 || data.scrapRatios.cement > 100) {
      errors.push('Cement scrap ratio must be between 0-100%');
    }
    if (data.fossilFuelDisplacement < 0 || data.fossilFuelDisplacement > 100) {
      errors.push('Fossil fuel displacement must be between 0-100%');
    }

    return errors;
  };

  // Handle manual input changes
  const handleInputChange = (field: keyof InputData | string, value: number) => {
    let newData = { ...inputData };
    
    if (field === 'steel' || field === 'cement') {
      newData.scrapRatios = { ...newData.scrapRatios, [field]: value };
    } else {
      newData = { ...newData, [field]: value };
    }
    
    // Real-time validation and calculation
    const errors = validateData(newData);
    setValidationErrors(errors);
    
    if (errors.length === 0) {
      const calculatedResults = calculateResults(newData);
      updateData(newData, calculatedResults);
    }
  };

  // Handle Excel file upload
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        // Parse the uploaded data (expecting specific format)
        if (jsonData.length > 0) {
          const row = jsonData[0] as any;
          const uploadedData: InputData = {
            rdfAvailable: parseFloat(row['RDF_Available_%'] || 0),
            biogasIntegration: parseFloat(row['Biogas_Integration_%'] || 0),
            clinkerSubstitution: parseFloat(row['Clinker_Substitution_%'] || 0),
            scrapRatios: {
              steel: parseFloat(row['Steel_Scrap_Ratio_%'] || 0),
              cement: parseFloat(row['Cement_Scrap_Ratio_%'] || 0)
            },
            fossilFuelDisplacement: parseFloat(row['Fossil_Fuel_Displacement_%'] || 0)
          };

          const errors = validateData(uploadedData);
          if (errors.length === 0) {
            const calculatedResults = calculateResults(uploadedData);
            updateData(uploadedData, calculatedResults);
            setUploadStatus('success');
            setValidationErrors([]);
          } else {
            setValidationErrors(errors);
            setUploadStatus('error');
          }
        }
      } catch (error) {
        setUploadStatus('error');
        setValidationErrors(['Error reading Excel file. Please check the format.']);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Sample datasets
  const sampleDatasets = [
    {
      name: "Conservative Scenario",
      description: "Low-risk approach with minimal changes to existing operations",
      data: {
        'RDF_Available_%': 15,
        'Biogas_Integration_%': 10,
        'Clinker_Substitution_%': 20,
        'Steel_Scrap_Ratio_%': 25,
        'Cement_Scrap_Ratio_%': 15,
        'Fossil_Fuel_Displacement_%': 12
      },
      color: "bg-yellow-50 border-yellow-200 text-yellow-800"
    },
    {
      name: "Moderate Decarbonization",
      description: "Balanced approach with significant emission reductions",
      data: {
        'RDF_Available_%': 35,
        'Biogas_Integration_%': 25,
        'Clinker_Substitution_%': 40,
        'Steel_Scrap_Ratio_%': 45,
        'Cement_Scrap_Ratio_%': 30,
        'Fossil_Fuel_Displacement_%': 38
      },
      color: "bg-blue-50 border-blue-200 text-blue-800"
    },
    {
      name: "Aggressive Net-Zero Strategy",
      description: "Maximum decarbonization with advanced technology integration",
      data: {
        'RDF_Available_%': 65,
        'Biogas_Integration_%': 55,
        'Clinker_Substitution_%': 70,
        'Steel_Scrap_Ratio_%': 80,
        'Cement_Scrap_Ratio_%': 60,
        'Fossil_Fuel_Displacement_%': 75
      },
      color: "bg-green-50 border-green-200 text-green-800"
    }
  ];

  // Download Excel template
  const downloadTemplate = () => {
    const templateData = [
      {
        'RDF_Available_%': 25,
        'Biogas_Integration_%': 15,
        'Clinker_Substitution_%': 30,
        'Steel_Scrap_Ratio_%': 20,
        'Cement_Scrap_Ratio_%': 25,
        'Fossil_Fuel_Displacement_%': 35
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data_Input_Template');
    
    // Add validation and formatting
    ws['!cols'] = [
      { wch: 20 }, { wch: 20 }, { wch: 20 },
      { wch: 20 }, { wch: 20 }, { wch: 25 }
    ];
    
    XLSX.writeFile(wb, 'MRV_Data_Input_Template.xlsx');
  };

  // Download sample dataset
  const downloadSampleDataset = (dataset: typeof sampleDatasets[0]) => {
    const ws = XLSX.utils.json_to_sheet([dataset.data]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sample_Data');
    
    // Add metadata sheet
    const metadata = [{
      'Scenario_Name': dataset.name,
      'Description': dataset.description,
      'Generated_On': new Date().toLocaleDateString(),
      'Notes': 'Sample dataset for MRV Climate Platform testing'
    }];
    
    const metaWs = XLSX.utils.json_to_sheet(metadata);
    XLSX.utils.book_append_sheet(wb, metaWs, 'Metadata');
    
    // Set column widths
    ws['!cols'] = [
      { wch: 20 }, { wch: 20 }, { wch: 20 },
      { wch: 20 }, { wch: 20 }, { wch: 25 }
    ];
    
    metaWs['!cols'] = [
      { wch: 20 }, { wch: 50 }, { wch: 15 }, { wch: 40 }
    ];
    
    const fileName = `${dataset.name.replace(/\s+/g, '_')}_Sample.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Load sample data directly
  const loadSampleData = (dataset: typeof sampleDatasets[0]) => {
    const uploadedData: InputData = {
      rdfAvailable: dataset.data['RDF_Available_%'],
      biogasIntegration: dataset.data['Biogas_Integration_%'],
      clinkerSubstitution: dataset.data['Clinker_Substitution_%'],
      scrapRatios: {
        steel: dataset.data['Steel_Scrap_Ratio_%'],
        cement: dataset.data['Cement_Scrap_Ratio_%']
      },
      fossilFuelDisplacement: dataset.data['Fossil_Fuel_Displacement_%']
    };

    const errors = validateData(uploadedData);
    if (errors.length === 0) {
      const calculatedResults = calculateResults(uploadedData);
      updateData(uploadedData, calculatedResults);
      setScenarioName(dataset.name);
      setUploadStatus('success');
      setValidationErrors([]);
    }
  };

  // Generate and download report
  const generateReport = () => {
    if (!results) return;

    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(20);
    doc.text('MRV Climate Impact Report', 20, 30);
    
    doc.setFontSize(12);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 45);
    
    // Input Parameters
    doc.setFontSize(16);
    doc.text('Input Parameters', 20, 65);
    doc.setFontSize(10);
    doc.text(`RDF Available: ${inputData.rdfAvailable}%`, 20, 75);
    doc.text(`Biogas Integration: ${inputData.biogasIntegration}%`, 20, 85);
    doc.text(`Clinker Substitution: ${inputData.clinkerSubstitution}%`, 20, 95);
    doc.text(`Steel Scrap Ratio: ${inputData.scrapRatios.steel}%`, 20, 105);
    doc.text(`Cement Scrap Ratio: ${inputData.scrapRatios.cement}%`, 20, 115);
    doc.text(`Fossil Fuel Displacement: ${inputData.fossilFuelDisplacement}%`, 20, 125);
    
    // Results
    doc.setFontSize(16);
    doc.text('MRV Summary', 20, 145);
    doc.setFontSize(10);
    doc.text(`Total CO₂ Savings: ${results.co2Savings.toFixed(2)} tCO₂e`, 20, 155);
    
    doc.setFontSize(16);
    doc.text('Financial Outcomes', 20, 175);
    doc.setFontSize(10);
    doc.text(`Cost Savings: $${results.financialOutcomes.costSavings.toLocaleString()}`, 20, 185);
    doc.text(`Revenue: $${results.financialOutcomes.revenue.toLocaleString()}`, 20, 195);
    doc.text(`Net Benefit: $${results.financialOutcomes.netBenefit.toLocaleString()}`, 20, 205);
    
    doc.setFontSize(16);
    doc.text('Credit/ITMO Potential', 20, 225);
    doc.setFontSize(10);
    doc.text(`Carbon Credits: ${results.creditPotential.carbonCredits.toFixed(2)} tCO₂e`, 20, 235);
    doc.text(`ITMO Value: $${results.creditPotential.itmoValue.toLocaleString()}`, 20, 245);
    
    // SDG Impact
    doc.setFontSize(16);
    doc.text('SDG Impact Assessment', 20, 265);
    doc.setFontSize(10);
    doc.text('• SDG 7: Affordable and Clean Energy - Enhanced through biogas integration', 20, 275);
    doc.text('• SDG 9: Industry, Innovation and Infrastructure - Circular economy practices', 20, 285);
    doc.text('• SDG 13: Climate Action - Direct CO₂ emission reductions', 110, 275);
    doc.text('• SDG 12: Responsible Production - Waste-to-energy conversion', 110, 285);
    
    doc.save('MRV_Climate_Report.pdf');
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Data Upload & Analytics</h1>
          <button
            onClick={downloadTemplate}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Download className="h-4 w-4 mr-2" />
            Download Template
          </button>
        </div>

        {/* Upload Section */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Upload Data</h2>
          
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
            <div className="text-center">
              <FileSpreadsheet className="mx-auto h-12 w-12 text-gray-400" />
              <div className="mt-4">
                <label htmlFor="file-upload" className="cursor-pointer">
                  <span className="mt-2 block text-sm font-medium text-gray-900">
                    Upload Excel file or drag and drop
                  </span>
                  <input
                    id="file-upload"
                    name="file-upload"
                    type="file"
                    className="sr-only"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                  />
                </label>
                <p className="mt-2 text-xs text-gray-500">
                  XLSX or XLS files only
                </p>
              </div>
            </div>
          </div>

          {uploadStatus === 'success' && (
            <div className="mt-4 flex items-center text-green-600">
              <CheckCircle2 className="h-5 w-5 mr-2" />
              Data uploaded successfully!
            </div>
          )}

          {uploadStatus === 'error' && validationErrors.length > 0 && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-center text-red-600 mb-2">
                <AlertCircle className="h-5 w-5 mr-2" />
                Validation Errors:
              </div>
              <ul className="text-sm text-red-600 list-disc list-inside">
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Sample Datasets Section */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Sample Datasets - Test Different Scenarios</h2>
          <p className="text-sm text-gray-600 mb-6">
            Try these pre-configured datasets to explore different decarbonization strategies and their impact on CO₂ savings and financial outcomes.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {sampleDatasets.map((dataset, index) => (
              <div key={index} className={`p-4 border rounded-lg ${dataset.color}`}>
                <h3 className="font-semibold mb-2">{dataset.name}</h3>
                <p className="text-sm mb-4">{dataset.description}</p>
                
                {/* Key metrics preview */}
                <div className="space-y-1 text-xs mb-4">
                  <div className="flex justify-between">
                    <span>RDF:</span>
                    <span>{dataset.data['RDF_Available_%']}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Biogas:</span>
                    <span>{dataset.data['Biogas_Integration_%']}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Clinker Sub:</span>
                    <span>{dataset.data['Clinker_Substitution_%']}%</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Steel Scrap:</span>
                    <span>{dataset.data['Steel_Scrap_Ratio_%']}%</span>
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={() => loadSampleData(dataset)}
                    className="flex items-center px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded text-sm hover:bg-gray-50"
                  >
                    <PlayCircle className="h-3 w-3 mr-1" />
                    Load Data
                  </button>
                  <button
                    onClick={() => downloadSampleDataset(dataset)}
                    className="flex items-center px-3 py-1.5 bg-white text-gray-700 border border-gray-300 rounded text-sm hover:bg-gray-50"
                  >
                    <FileDown className="h-3 w-3 mr-1" />
                    Download
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Manual Input Section */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Manual Data Entry</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                RDF Available (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={inputData.rdfAvailable}
                onChange={(e) => handleInputChange('rdfAvailable', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Biogas Integration (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={inputData.biogasIntegration}
                onChange={(e) => handleInputChange('biogasIntegration', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Clinker Substitution (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={inputData.clinkerSubstitution}
                onChange={(e) => handleInputChange('clinkerSubstitution', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Steel Scrap Ratio (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={inputData.scrapRatios.steel}
                onChange={(e) => handleInputChange('steel', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cement Scrap Ratio (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={inputData.scrapRatios.cement}
                onChange={(e) => handleInputChange('cement', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0-100"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fossil Fuel Displacement (%)
              </label>
              <input
                type="number"
                min="0"
                max="100"
                value={inputData.fossilFuelDisplacement}
                onChange={(e) => handleInputChange('fossilFuelDisplacement', parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="0-100"
              />
            </div>
          </div>
        </div>

        {/* Results Section */}
        {results && (
          <div className="space-y-6">
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">CO₂ Savings</p>
                    <p className="text-2xl font-bold text-green-600">{results.co2Savings.toFixed(2)} tCO₂e</p>
                  </div>
                  <Leaf className="h-8 w-8 text-green-600" />
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Net Financial Benefit</p>
                    <p className="text-2xl font-bold text-blue-600">${results.financialOutcomes.netBenefit.toLocaleString()}</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-blue-600" />
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">ITMO Value</p>
                    <p className="text-2xl font-bold text-purple-600">${results.creditPotential.itmoValue.toLocaleString()}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-purple-600" />
                </div>
              </div>
            </div>

            {/* Detailed Results */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Breakdown</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cost Savings</span>
                    <span className="font-semibold">${results.financialOutcomes.costSavings.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Carbon Revenue</span>
                    <span className="font-semibold">${results.financialOutcomes.revenue.toLocaleString()}</span>
                  </div>
                  <div className="border-t pt-2">
                    <div className="flex justify-between font-bold">
                      <span>Total Benefit</span>
                      <span className="text-green-600">${results.financialOutcomes.netBenefit.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Credit Potential</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Carbon Credits</span>
                    <span className="font-semibold">{results.creditPotential.carbonCredits.toFixed(2)} tCO₂e</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Credit Value (@$45/tCO₂e)</span>
                    <span className="font-semibold">${(results.creditPotential.carbonCredits * 45).toLocaleString()}</span>
                  </div>
                  <div className="border-t pt-2">
                    <div className="flex justify-between font-bold">
                      <span>ITMO Premium Value</span>
                      <span className="text-purple-600">${results.creditPotential.itmoValue.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Generate Report Button */}
            <div className="flex justify-center">
              <button
                onClick={generateReport}
                className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
              >
                <Download className="h-5 w-5 mr-2" />
                Generate & Download Report
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default DataUpload;
